import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Calendar, Clock, Target, Award, Zap, ArrowUpRight, ArrowDownRight, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserManifestations } from '../lib/supabase';
import { useTranslation } from '../hooks/useTranslation';

interface AnalyticsData {
  totalManifestations: number;
  manifestedCount: number;
  successRate: number;
  activeManifestations: number;
  mostActiveDay: string;
  mostActiveHour: string;
  manifestationTrend: 'up' | 'down' | 'stable';
  trendPercentage: number;
  topCategory: string;
  streakDays: number;
  weeklyActivity: Record<string, number>;
  hourlyActivity: Record<string, number>;
  manifestationsByCategory: Record<string, number>;
  recentSuccesses: string[];
}

export const AnalyticsInsights: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'patterns' | 'insights'>('overview');

  useEffect(() => {
    if (user) {
      fetchAnalyticsData();
    }
  }, [user]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // In a real app, this would fetch from an API
      // For now, we'll generate mock data
      
      // Try to get real manifestations if available
      let manifestations = [];
      try {
        if (user) {
          manifestations = await getUserManifestations(user.id);
        }
      } catch (error) {
        console.error('Error fetching manifestations:', error);
      }
      
      // Generate mock analytics data
      const mockData: AnalyticsData = {
        totalManifestations: manifestations.length || Math.floor(Math.random() * 30) + 5,
        manifestedCount: Math.floor(Math.random() * 15) + 2,
        successRate: Math.floor(Math.random() * 40) + 30,
        activeManifestations: Math.floor(Math.random() * 10) + 1,
        mostActiveDay: ['Monday', 'Wednesday', 'Sunday'][Math.floor(Math.random() * 3)],
        mostActiveHour: ['Morning (6-9 AM)', 'Evening (7-10 PM)'][Math.floor(Math.random() * 2)],
        manifestationTrend: ['up', 'down', 'stable'][Math.floor(Math.random() * 3)] as 'up' | 'down' | 'stable',
        trendPercentage: Math.floor(Math.random() * 20) + 5,
        topCategory: ['Health & Wellness', 'Money & Abundance', 'Relationships & Love'][Math.floor(Math.random() * 3)],
        streakDays: Math.floor(Math.random() * 30) + 1,
        weeklyActivity: {
          'Mon': Math.floor(Math.random() * 10),
          'Tue': Math.floor(Math.random() * 10),
          'Wed': Math.floor(Math.random() * 10),
          'Thu': Math.floor(Math.random() * 10),
          'Fri': Math.floor(Math.random() * 10),
          'Sat': Math.floor(Math.random() * 10),
          'Sun': Math.floor(Math.random() * 10),
        },
        hourlyActivity: {
          'Morning': Math.floor(Math.random() * 30) + 10,
          'Afternoon': Math.floor(Math.random() * 30) + 5,
          'Evening': Math.floor(Math.random() * 30) + 15,
          'Night': Math.floor(Math.random() * 30),
        },
        manifestationsByCategory: {
          'Health & Wellness': Math.floor(Math.random() * 20) + 5,
          'Money & Abundance': Math.floor(Math.random() * 20) + 5,
          'Relationships & Love': Math.floor(Math.random() * 20) + 5,
          'Career & Success': Math.floor(Math.random() * 20) + 5,
          'Personal Growth': Math.floor(Math.random() * 20) + 5,
        },
        recentSuccesses: [
          'Attracted a new job opportunity',
          'Improved health and energy levels',
          'Manifested a meaningful relationship',
          'Created financial abundance',
          'Developed a consistent meditation practice'
        ].slice(0, Math.floor(Math.random() * 3) + 1)
      };

      // Mark analytics as viewed for today (for notification system)
      localStorage.setItem(`analytics_viewed_${new Date().toISOString().split('T')[0]}`, 'true');
      
      setAnalyticsData(mockData);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">{t('profile.advancedAnalyticsInsights')}</h2>
        <button 
          onClick={fetchAnalyticsData}
          className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center space-x-1"
        >
          <Zap size={14} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('overview')}
          className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'overview' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('patterns')}
          className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'patterns' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Patterns
        </button>
        <button
          onClick={() => setActiveTab('insights')}
          className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'insights' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Insights
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Manifestations */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Sparkles size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Manifestations</p>
                  <p className="text-2xl font-bold text-gray-900">{analyticsData.totalManifestations}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center space-x-1 text-sm ${
                    analyticsData.manifestationTrend === 'up' 
                      ? 'text-green-600' 
                      : analyticsData.manifestationTrend === 'down' 
                        ? 'text-red-600' 
                        : 'text-gray-600'
                  }`}>
                    {analyticsData.manifestationTrend === 'up' && <ArrowUpRight size={14} />}
                    {analyticsData.manifestationTrend === 'down' && <ArrowDownRight size={14} />}
                    <span>{analyticsData.manifestationTrend === 'stable' ? 'Stable' : `${analyticsData.trendPercentage}% ${analyticsData.manifestationTrend}`}</span>
                  </div>
                  <span className="text-xs text-gray-500">vs. last month</span>
                </div>
              </div>
            </div>

            {/* Success Rate */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Target size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Success Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{analyticsData.successRate}%</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full" 
                    style={{ width: `${Math.min(100, analyticsData.successRate)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {analyticsData.manifestedCount} manifested out of {analyticsData.totalManifestations}
                </p>
              </div>
            </div>

            {/* Streak */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Zap size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Streak</p>
                  <p className="text-2xl font-bold text-gray-900">{analyticsData.streakDays} days</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center space-x-2">
                  <Award size={14} className="text-amber-600" />
                  <span className="text-xs text-gray-600">
                    {analyticsData.streakDays >= 30 
                      ? 'Meditation unlocked!' 
                      : `${30 - analyticsData.streakDays} more days to unlock meditation`}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Successes */}
          {analyticsData.recentSuccesses.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Manifestation Successes</h3>
              <div className="space-y-3">
                {analyticsData.recentSuccesses.map((success, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check size={16} className="text-white" />
                    </div>
                    <span className="text-green-800">{success}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Categories */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Manifestation Categories</h3>
            <div className="space-y-4">
              {Object.entries(analyticsData.manifestationsByCategory).map(([category, count]) => {
                // Calculate percentage but ensure it doesn't exceed 100%
                const percentage = Math.min(100, (count / analyticsData.totalManifestations) * 100);
                
                return (
                  <div key={category} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">{category}</span>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          category === 'Health & Wellness' ? 'bg-green-500' :
                          category === 'Money & Abundance' ? 'bg-amber-500' :
                          category === 'Relationships & Love' ? 'bg-pink-500' :
                          category === 'Career & Success' ? 'bg-blue-500' :
                          'bg-purple-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Patterns Tab */}
      {activeTab === 'patterns' && (
        <div className="space-y-6">
          {/* Weekly Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Activity Pattern</h3>
            <div className="flex items-end h-40 space-x-2">
              {Object.entries(analyticsData.weeklyActivity).map(([day, value]) => {
                // Ensure height doesn't exceed 100%
                const height = Math.min(100, (value / 10) * 100);
                return (
                  <div key={day} className="flex-1 flex flex-col items-center">
                    <div 
                      className={`w-full rounded-t-md ${
                        day === analyticsData.mostActiveDay ? 'bg-purple-600' : 'bg-purple-200'
                      }`}
                      style={{ height: `${height}%` }}
                    ></div>
                    <span className="text-xs text-gray-500 mt-2">{day}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-purple-600" />
                <span className="text-sm text-gray-700">
                  Most active day: <span className="font-medium">{analyticsData.mostActiveDay}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Daily Timing */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Timing Pattern</h3>
            <div className="space-y-4">
              {Object.entries(analyticsData.hourlyActivity).map(([timeOfDay, value]) => {
                // Ensure percentage doesn't exceed 100%
                const percentage = Math.min(100, (value / 30) * 100);
                return (
                  <div key={timeOfDay} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">{timeOfDay}</span>
                      <span className="text-sm font-medium text-gray-900">{value}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          timeOfDay === 'Morning' ? 'bg-amber-500' :
                          timeOfDay === 'Afternoon' ? 'bg-orange-500' :
                          timeOfDay === 'Evening' ? 'bg-purple-500' :
                          'bg-indigo-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <Clock size={16} className="text-purple-600" />
                <span className="text-sm text-gray-700">
                  Peak manifestation time: <span className="font-medium">{analyticsData.mostActiveHour}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Manifestation Cycle */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Manifestation Cycle</h3>
            <div className="relative">
              <div className="w-full h-60 flex items-center justify-center">
                <div className="w-48 h-48 rounded-full border-4 border-purple-100 relative">
                  {/* New Moon */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                    <p className="text-xs text-gray-600 mt-1 text-center">New Intention</p>
                  </div>
                  
                  {/* Waxing Crescent */}
                  <div className="absolute top-1/4 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 bg-purple-400 rounded-full"></div>
                    <p className="text-xs text-gray-600 mt-1 text-center">Clarify</p>
                  </div>
                  
                  {/* Full Moon */}
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
                    <div className="w-4 h-4 bg-purple-800 rounded-full"></div>
                    <p className="text-xs text-gray-600 mt-1 text-center">Manifestation</p>
                  </div>
                  
                  {/* Waning Crescent */}
                  <div className="absolute top-1/4 left-0 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 bg-purple-300 rounded-full"></div>
                    <p className="text-xs text-gray-600 mt-1 text-center">Release</p>
                  </div>
                  
                  {/* Current Position (example) */}
                  <div className="absolute top-1/3 right-1/4 transform translate-x-1/2 -translate-y-1/2">
                    <div className="w-6 h-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full animate-pulse"></div>
                    <p className="text-xs font-medium text-purple-700 mt-1">You are here</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Your manifestation cycle shows you're in the <span className="font-medium text-purple-700">growth phase</span>. 
                This is an ideal time to focus on clarity and visualization.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Insights Tab */}
      {activeTab === 'insights' && (
        <div className="space-y-6">
          {/* Personalized Insights */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Manifestation Insights</h3>
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <TrendingUp size={16} className="text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-purple-900">Optimal Manifestation Time</h4>
                    <p className="text-sm text-purple-700 mt-1">
                      Your manifestations are 42% more effective when created in the {analyticsData.mostActiveHour.toLowerCase()}. 
                      Consider setting aside dedicated manifestation time during this period.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Target size={16} className="text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-green-900">Category Focus</h4>
                    <p className="text-sm text-green-700 mt-1">
                      You've had the most success with manifestations in the "{analyticsData.topCategory}" category. 
                      Consider focusing more energy here while building momentum in other areas.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Calendar size={16} className="text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-amber-900">Consistency Pattern</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Your manifestation practice is most consistent on {analyticsData.mostActiveDay}s. 
                      Consider creating a ritual on this day to amplify your manifestation power.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personalized Recommendations</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-lg">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles size={18} className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-blue-900">Enhance Your Practice</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Based on your patterns, try adding a 5-minute visualization practice to your {analyticsData.mostActiveDay} routine.
                    This simple addition could increase your manifestation success rate by up to 15%.
                  </p>
                  <button className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded-full hover:bg-blue-700 transition-colors duration-200">
                    Learn More
                  </button>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-pink-50 rounded-lg">
                <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart size={18} className="text-pink-600" />
                </div>
                <div>
                  <h4 className="font-medium text-pink-900">Gratitude Connection</h4>
                  <p className="text-sm text-pink-700 mt-1">
                    Users who practice gratitude daily have a 27% higher manifestation success rate.
                    Consider adding 3 gratitude entries each morning to amplify your manifestation energy.
                  </p>
                  <button className="mt-2 text-xs bg-pink-600 text-white px-3 py-1 rounded-full hover:bg-pink-700 transition-colors duration-200">
                    Start Practice
                  </button>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-lg">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap size={18} className="text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-purple-900">Power Words</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    Your most successful manifestations contain powerful words like "abundant," "effortlessly," and "joy."
                    Try incorporating these words more frequently in your manifestation practice.
                  </p>
                  <button className="mt-2 text-xs bg-purple-600 text-white px-3 py-1 rounded-full hover:bg-purple-700 transition-colors duration-200">
                    View Word List
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Upgrade Prompt */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Award size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Unlock Advanced Insights</h3>
                <p className="text-white/90 mb-4">
                  Upgrade to Abundant Creator to access our most powerful analytics, including predictive manifestation timing, 
                  energy alignment tracking, and personalized manifestation coaching.
                </p>
                <button className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-white/90 transition-colors duration-200">
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for the check icon
const Check: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);